import { isRedoEmailAddress } from "@redotech/account-common/redo-domains";

export const ADSI_SHORTCUT = "*adsi";
export const SHORTCUT_IDLE_MS = 2000;
export const ADSI_STYLE_ELEMENT_ID = "arbiter-design-system-inspector-css";

const MIN_VISIBLE_PX = 48;

const TEXT_ENTRY_INPUT_TYPES: ReadonlySet<string> = new Set([
  "",
  "email",
  "number",
  "password",
  "search",
  "tel",
  "text",
  "url",
]);

export const designSystemLayers = [
  { id: "arbiter", label: "Arbiter components" },
  { id: "web", label: "Non-Arbiter web components" },
  { id: "text-header", label: "Text and Header" },
  { id: "flex", label: "Flex" },
] as const;

export type DesignSystemLayer = (typeof designSystemLayers)[number]["id"];

const outsideInspector =
  ':not([data-testid="arbiter-design-system-inspector"], [data-testid="arbiter-design-system-inspector"] *)';

const successOutline = "var(--redo-colors-foreground-fg-success-primary)";
const errorOutline = "var(--redo-colors-foreground-fg-error-primary)";

const layerCss: Record<DesignSystemLayer, string> = {
  arbiter: `[data-arbiter]:not([data-arbiter="Flex"], [data-arbiter="Text"], [data-arbiter="Header"])${outsideInspector} {
    outline: solid ${successOutline} 2px;
}`,
  flex: `[data-arbiter="Flex"]${outsideInspector} {
    outline: solid ${successOutline} 2px;
}`,
  "text-header": `[data-arbiter="Text"]${outsideInspector} {
    outline: solid ${successOutline} 2px;
}

[data-arbiter="Header"]${outsideInspector} {
    outline: solid ${successOutline} 2px;
}`,
  web: `[data-web]${outsideInspector} {
    outline: solid ${errorOutline} 2px;
}`,
};

export function canUseDesignSystemInspector(
  email: string | null | undefined,
  nodeEnv: string | undefined = process.env.NODE_ENV,
): boolean {
  return nodeEnv === "development" || isRedoEmailAddress(email);
}

export function designSystemInspectorCss(
  enabled: ReadonlySet<DesignSystemLayer>,
): string {
  const order: DesignSystemLayer[] = ["arbiter", "flex", "text-header", "web"];
  return order
    .filter((layer) => enabled.has(layer))
    .map((layer) => layerCss[layer])
    .join("\n\n");
}

export function advanceShortcutBuffer(
  buffer: string,
  key: string,
): { buffer: string; matched: boolean } {
  if (key.length !== 1) {
    return { buffer, matched: false };
  }
  const candidate = `${buffer}${key}`;
  if (ADSI_SHORTCUT.startsWith(candidate)) {
    const matched = candidate === ADSI_SHORTCUT;
    return { buffer: matched ? "" : candidate, matched };
  }
  if (ADSI_SHORTCUT.startsWith(key)) {
    return { buffer: key, matched: false };
  }
  return { buffer: "", matched: false };
}

export function isTextEntryTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  if (
    target.isContentEditable ||
    target.contentEditable === "true" ||
    target.closest("[contenteditable='true'], [contenteditable='']")
  ) {
    return true;
  }
  if (
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement
  ) {
    return true;
  }
  if (target instanceof HTMLInputElement) {
    return TEXT_ENTRY_INPUT_TYPES.has(target.type);
  }
  return false;
}

export function clampDialogPosition(
  left: number,
  top: number,
  width: number,
  viewport: { width: number; height: number },
): { left: number; top: number } {
  const minLeft = MIN_VISIBLE_PX - width;
  const maxLeft = Math.max(minLeft, viewport.width - MIN_VISIBLE_PX);
  const maxTop = Math.max(0, viewport.height - MIN_VISIBLE_PX);
  return {
    left: Math.min(Math.max(left, minLeft), maxLeft),
    top: Math.min(Math.max(top, 0), maxTop),
  };
}
